import { ApplicationElement, html } from '../application_element/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import CommonActions from '../common_actions/element.js';
import '../icon/element.js';

/**
 * This element has two modes: show and edit. Both modes get template from backend and show it inside default slot. If mode is edit, a form is expected to be in template.
 * On submit of that form, backend returns new template for default slot (can be a form again - with validation errors, or new show mode template).
 *
 * Element expects returned (or initially slotted) template to respect the following rules:
 * - Submit button can be button or input and must have attribute type="submit"
 * - Cancel button (restores cached version of show) must have a class js-inline-cancel
 * - Edit template form should have attributes: action (url for sending form data - required) and method (method used for sending form data - optional, default='get')
 * - If you want an input to be focused when the edit form loads - add autofocus attribute to it
 *
 * @prop formTitle - Title of the inline form
 * @prop showUrl - Url for getting show template
 * @prop editUrl - Url for getting edit template (the form)
 * @prop namespace - Namespace, used for dispatching events (optional)
 * @prop showIcon - Header icon that appears when we are in edit mode (optional, default: chevron-up)
 * @prop editIcon - Header icon that appears when we are in show mode (optional, default: chevron-down)
 * @prop showLabel - Text that appears next to showIcon (optional)
 * @prop editLabel - Text that appears next to editIcon (optional)
 *
 * @cssprop [--header-margin: 0] - Margin of the header
 * @cssprop [--header-padding: 0] - Padding of the header
 * @cssprop [--header-color: var(--dark-text)] - Font color of the header
 * @cssprop [--header-border-top: none] - Top border of the header
 * @cssprop [--header-border-right: none] - Right border of the header
 * @cssprop [--header-border-bottom: none] - Bottom border of the header
 * @cssprop [--header-border-left: none] - Left border of the header
 * @cssprop [--header-items-gap: 16px] - Distance between heading and toggle button
 * @cssprop [--heading-font-size: var(--font-size-lg)] - Font size of heading
 * @cssprop [--heading-font-weight: 500] - Font weight of heading
 * @cssprop [--toggle-button-items-gap: 8px] - Distance between label and icon in the toggle button
 * @cssprop [--icon-font-size: var(--font-size-xl)] - Font size of the edit and show icons
 * @cssprop [--icon-color: var(--dark-text)] - Color of edit and show icons
 * @cssprop [--label-font-size: inherit] - Font size of the edit and show labels
 * @cssprop [--label-color: inherit] - Color of edit and show labels
 *
 * @slot default - For the initial show content
 */

export default class InlineForm extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    formTitle: { type: String },
    showUrl: { type: String },
    editUrl: { type: String },
    namespace: { type: String },
    showIcon: { type: String },
    editIcon: { type: String },
    showLabel: { type: String },
    editLabel: { type: String },
    open: { type: Boolean, state: true },
    loading: { type: Boolean, state: true },
    cachedShowContent: { type: String, state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.editIcon = 'chevron-down';
    this.showIcon = 'chevron-up';
    this.commonActions = new CommonActions(this);
  }

  firstUpdated() {
    this.cachedShowContent = this.innerHTML;
  }

  /** @internal */
  get form() {
    return this.querySelector('form');
  }

  /** @internal */
  get submitButton() {
    return this.form.querySelector("[type='submit']");
  }

  /** @internal */
  get icon() {
    return this.open ? this.showIcon : this.editIcon;
  }

  /** @internal */
  get hasLabel() {
    return (this.open && this.showLabel) || (!this.open && this.editLabel);
  }

  errorSnack() {
    this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
  }

  async fetchTemplate(url, method = 'get', opts = {}) {
    const res = await this.commonActions[method](url, opts);
    this.innerHTML = await res.text();
  }

  toggleOpen() {
    this.open ? this.restoreShow() : this.loadEdit();
  }

  autofocusForm() {
    this.form.querySelector('[autofocus]')?.focus();
  }

  async loadEdit() {
    try {
      await this.fetchTemplate(this.editUrl);
      this.autofocusForm();
      this.open = true;
      this.dispatchCustomEvent('change');
    } catch {
      this.errorSnack();
    }
  }

  async reloadShow() {
    try {
      await this.fetchTemplate(this.showUrl);
      this.cachedShowContent = this.innerHTML;
      this.open = false;
      this.dispatchCustomEvent('change');
    } catch {
      this.errorSnack();
    }
  }

  restoreShow() {
    this.innerHTML = this.cachedShowContent;
    this.open = false;
    this.dispatchCustomEvent('change');
  }

  handleClick(e) {
    if (e.target.closest('.js-inline-cancel')) this.restoreShow();
  }

  async handleSubmit(e) {
    e?.preventDefault();
    if (this.loading) return;

    this.loading = true;
    this.submitButton.disabled = true;

    try {
      await this.fetchTemplate(this.form.action, this.form.method, {
        body: new FormData(this.form),
      });

      this.dispatchCustomEvent('submit');

      if (this.form) {
        // Unsuccessful validation, new form returned
        // this.autofocusForm(); // If input is focused the error message is not visible. Can be uncomment once we move validation messages below the input.
      } else {
        // Successful validation, new show returned
        this.cachedShowContent = this.innerHTML;
        this.open = false;
      }
    } catch {
      this.submitButton.disabled = false;
      this.errorSnack();
    } finally {
      this.loading = false;
    }
  }

  dispatchCustomEvent(name) {
    this.dispatchEvent(
      new Event(this.eventName(name), { bubbles: true, composed: true })
    );
  }

  eventName(name) {
    const eventNames = ['inlineForm', name];
    this.namespace && eventNames.push(this.namespace);

    return eventNames.join('-');
  }

  renderLabel() {
    return html`
      <span class="label">${this.open ? this.showLabel : this.editLabel}</span>
    `;
  }

  render() {
    return html`
      <header>
        <h3>${this.formTitle}</h3>
        <button @click=${this.toggleOpen} aria-label="edit">
          ${this.hasLabel ? this.renderLabel() : null}
          <ef-icon name=${this.icon}></ef-icon>
        </button>
      </header>
      <slot @submit=${this.handleSubmit} @click=${this.handleClick}></slot>
    `;
  }
}

InlineForm.registerTagName('ef-inline-form');

customElements.get('ef-inline-form') ||
  customElements.define('ef-inline-form', InlineForm);
